<template>
  <div class="upload">
    <div class="upload__info">
      <h3 class="upload__title">
        {{ label }}
      </h3>
      <div
        v-if="hasSampleFile || description || $slots.description"
        class="upload__hint flex gap-1 items-center"
      >
        <slot v-if="$slots.description"
              name="description" />
        <template v-else>
          {{ description }}
        </template>
        <bv-btn v-if="hasSampleFile"
                class="h-fit py-0 px-2"
                variant="text"
                @click="downloadSample">
          <vsx-icon icon-name="Import"
                    :size="16"
                    type="linear" />
          {{ $t('global.form.download_sample_file') }}
        </bv-btn>
      </div>
    </div>
    <div
      class="upload__wrapper"
      :class="[
        { 'upload__wrapper--disabled': disabled, 'upload__wrapper--error': error }
      ]"
    >
      <p class="upload__label"
         :class="[{ 'upload__label--error': error }]">
        {{ label }}
        <span v-if="maxLabel">({{ maxLabel }})</span>
        <span v-if="required"
              class="upload__required">*</span>
      </p>
      <div v-show="!modelValue && !disabled"
           class="upload__group">
        <upload-icon class="upload__icon" />
        <input
          ref="fileInput"
          :accept="$attrs.accept"
          hidden
          type="file"
          @change="handleFileUpload"
        />
        <button class="upload__btn"
                type="button"
                @click="triggerFileInput">
          {{ $t('global.form.upload') }}
        </button>
      </div>
      <div v-if="!(!modelValue && !disabled)"
           class="upload__file">
        <vsx-icon color="#808080"
                  icon-name="DocumentText1"
                  :size="24"
                  type="linear" />
        {{ modelValue?.name }}
        <button v-if="!disabled"
                class="upload__remove"
                type="button"
                @click="handleRemoveFile">
          <vsx-icon color="#999999"
                    icon-name="CloseCircle"
                    :size="16"
                    type="linear" />
        </button>
      </div>
    </div>
    <div v-if="error"
         class="upload__errors">
      <slot name="errors" />
    </div>
  </div>
</template>

<script>
  import { UploadIcon } from '@/components/Icons';

  export default {
    name: 'BevaUpload',
    components: {
      UploadIcon,
    },
    props: {
      modelValue: {
        type: null,
        required: true,
      },
      csvSampleContent: {
        type: String,
        default: undefined,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        required: true,
      },
      description: {
        type: String,
        default: '',
      },
      hasSampleFile: {
        type: Boolean,
        default: true,
      },
      maxLabel: {
        type: String,
        default: '',
      },
      error: {
        type: Boolean,
        default: false,
      },
    },
    methods: {
      downloadSample() {
        const csvContent =
          '\uFEFF' +
          (this.csvSampleContent
            ? this.csvSampleContent
            : 'Phone,Name\n+1234567890,John Doe\n+9876543210,Jane Smith');
        const encodedUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
        const link = document.createElement('a');
        link.href = encodedUri;
        link.download = 'sample.csv';
        link.click();
        link.remove();
      },
      triggerFileInput() {
        this.$refs.fileInput.click();
      },
      handleFileUpload(event) {
        const file = event.target.files[0];
        this.$emit('update:modelValue', file);
      },
      handleRemoveFile() {
        // Update modelValue
        this.$emit('update:modelValue', null);
      },
    },
  };
</script>

<style lang="scss" scoped>
.upload {
  &__info {
    padding: 16px;
    border-radius: 8px;
    background-color: var(--neutral50, #f5f8fa);
    margin-block-end: 16px;
  }
  &__title {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px;
    color: var(--gray-900, #1a1a1a);
    margin-block-end: 8px;
  }
  &__hint {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    color: var(--gray500, #808080);
  }
  // &__sample {
  //   font-size: 14px;
  //   font-style: normal;
  //   font-weight: 400;
  //   line-height: 14px;
  //   color: var(--color-base-500);
  //   margin-inline-start: 8px;
  // }
  &__wrapper {
    padding: 16px;
    border-radius: 8px;
    border: 1px solid var(--gray500, #808080);
    background-color: var(--shadesWhite, #fff);
    position: relative;
    &--disabled {
      background-color: var(--gray50, #f2f2f2);
    }
    &--error {
      border-color: var(--error-500, #ff3b30);
    }
  }
  &__label {
    position: absolute;
    inset-inline-start: 12px;
    top: -13px;
    background: var(--light-shades-white, #fff);
    padding: 4px;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: var(--gray500, #808080);
    &--error {
      color: var(--error-500, #ff3b30);
    }
  }
  &__required {
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 12px;
    color: var(--error-500, #ff3b30);
  }
  &__group {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-radius: 8px;
    border: 1px dashed var(--color-base-300, #62b6e4);
    background: var(--shadesWhite, #fff);
  }
  &__btn {
    padding: 8px 16px;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    border-radius: 6px;
    background: var(--color-base-500, #095ea6);
    color: var(--shadesWhite, #fff);
  }
  &__file {
    display: flex;
    align-items: center;
    gap: 4px;
    max-width: 250px;
    position: relative;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid var(--neutral200, #c3d4e3);
    color: var(--gray900, #1a1a1a);
    background-color: var(--neutral50, #f5f8fa);
  }
  &__remove {
    position: absolute;
    right: 6px;
    top: 6px;
    cursor: pointer;
  }
  &__errors {
    margin-block-start: 8px;
  }
  &__icon {
    color: var(--color-base-500);
  }
  &__error {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    color: var(--error-500, #ff3b30);
  }
}
</style>
